import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSocket } from "../socket/SocketProvider";
import Modal from "./Modal";
import { appointmentCancel } from "../../service/videoCallService";
import { toast } from "react-toastify";
import { apiRequest } from "../../util/async/apiUtils";
import { stopAllCallMedia } from "../../util/stopAllCallMedia";
import { stopAllRegisteredMedia } from "../../util/mediaTrackRegistry";
import { isInstantAppointment } from "../../util/timezoneHelpers";
import { resolveConsultationPath } from "../../util/callPathHelpers";

import moment from "moment-timezone";

/**
 * Client-side "calling / waiting for consultant" modal.
 * Must leave this screen as soon as consultant answers (socket OR poll fallback).
 */
const JoinCallConfirmationModal = ({ isOpen, setIsOpen, appointmentData }) => {
  const profileData = JSON.parse(localStorage.getItem("userDetails") || "{}");
  const [consultantJoined, setConsultantJoined] = useState(false);
  const navigate = useNavigate();
  const socket = useSocket();
  const appointmentRef = useRef(appointmentData);
  const joinedRef = useRef(false);

  const [isConsultantTesting, setIsConsultantTesting] = useState(false);

  useSocket(
    "appointmentPingResponse",
    (data) => {
      const waitingId =
        appointmentRef.current?.appointmentId ||
        JSON.parse(localStorage.getItem("appointmentData") || "{}")?.appointmentId;
      if (data?.appointmentId && String(data.appointmentId) === String(waitingId)) {
        if (data?.testingDevice === true) {
          setIsConsultantTesting(true);
        } else if (data?.testingDevice === false) {
          setIsConsultantTesting(false);
        }
      }
    },
    [isOpen]
  );

  useEffect(() => {
    appointmentRef.current = appointmentData;
  }, [appointmentData]);

  const goToCall = useCallback(
    (appointmentId) => {
      if (joinedRef.current) return;
      if (!appointmentId) return;
      joinedRef.current = true;
      setConsultantJoined(true);
      localStorage.setItem("joined", "true");

      const stored = JSON.parse(localStorage.getItem("appointmentData") || "{}");
      const base = appointmentRef.current || stored || {};
      localStorage.setItem(
        "appointmentData",
        JSON.stringify({
          ...base,
          appointmentId,
        }),
      );

      sessionStorage.removeItem(`prontopsy_rejoin_wait_${appointmentId}`);
      if (!localStorage.getItem(`actualConnectedSeconds_${appointmentId}`)) {
        localStorage.setItem(`actualConnectedSeconds_${appointmentId}`, "0");
      }
      try {
        localStorage.removeItem("actualConnectedSeconds");
      } catch (_) {}

      setIsOpen(false);
      navigate(resolveConsultationPath(appointmentId, "video"), { replace: true });
    },
    [navigate, setIsOpen],
  );

  // Join room while waiting
  useEffect(() => {
    if (!isOpen) {
      joinedRef.current = false;
      setConsultantJoined(false);
      return;
    }
    const appointmentId =
      appointmentData?.appointmentId ||
      JSON.parse(localStorage.getItem("appointmentData") || "{}")?.appointmentId;
    if (socket?.connected && appointmentId) {
      socket.emit("joinAppointmentRoom", { appointmentId });
    }
  }, [isOpen, socket, socket?.connected, appointmentData?.appointmentId]);

  // Primary: socket event when consultant answers
  useSocket(
    "consultantJoinResponse",
    (data) => {
      if (!isOpen || joinedRef.current) return;
      const waitingId =
        appointmentRef.current?.appointmentId ||
        JSON.parse(localStorage.getItem("appointmentData") || "{}")?.appointmentId;
      const myId = String(profileData?._id || "");
      const clientId = String(
        data?.clientId ||
        data?.data?.user?.id ||
        data?.callerData?.user?.id ||
        "",
      );
      const sameAppointment =
        waitingId && data?.appointmentId && String(data.appointmentId) === String(waitingId);
      const isClient =
        myId && (clientId === myId || data?.joined === true);

      // Accept if same appointment OR explicit client match
      if (data?.joined === true && data?.appointmentId && (sameAppointment || (clientId && clientId === myId))) {
        goToCall(data.appointmentId);
      } else if (isClient && data?.appointmentId && sameAppointment) {
        goToCall(data.appointmentId);
      }
    },
    [isOpen, profileData?._id, goToCall],
  );

  // Fallback: poll appointment status every 2s (if socket event is missed)
  useEffect(() => {
    if (!isOpen || consultantJoined) return;
    const appointmentId =
      appointmentData?.appointmentId ||
      JSON.parse(localStorage.getItem("appointmentData") || "{}")?.appointmentId;
    if (!appointmentId) return;

    let cancelled = false;
    const poll = async () => {
      try {
        const res = await apiRequest("get", "appointment/get-already-in-call", {
          queries: { appointmentId },
        });
        const doc = res?.data?.data || res?.data?.docs || res?.data;
        if (cancelled || joinedRef.current) return;
        if (
          doc?.consultantStatus === "join" ||
          doc?.status === "inProgress" ||
          (Array.isArray(doc?.activeArray) && doc.activeArray.length >= 1 && doc?.consultantJoinTime)
        ) {
          goToCall(appointmentId);
        }
      } catch (e) {
        // ignore poll errors
      }
    };

    poll();
    const interval = setInterval(poll, 2000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isOpen, consultantJoined, appointmentData?.appointmentId, goToCall]);

  const handleJoinLater = useCallback(() => {
    setIsOpen(false);
    stopAllCallMedia();
    stopAllRegisteredMedia();
    const appt = appointmentRef.current || JSON.parse(localStorage.getItem("appointmentData") || "{}");
    const appointmentId = appt?.appointmentId;

    if (appointmentId && isInstantAppointment(appt)) {
      appointmentCancel({ appointmentId, closeReason: "no_show" });
    }

    if (socket) {
      const finalConsultantId = appt?.consultantId || appt?.callerData?.consultant?.id || appt?.consultant?.id || appt?.consultant?._id;
      socket.emit("vonageSessionReject", {
        disconnect: true,
        appointmentId,
        role: profileData?.roleCode || "user",
        consultantId: finalConsultantId,
        userId: appt?.callerData?.user?.id || profileData?._id,
        clientId: appt?.clientId || profileData?._id,
      });
      socket.emit("consultantBusy", {
        consultantId: finalConsultantId,
        userId: appt?.callerData?.user?.id || profileData?._id,
        isBusy: false,
      });
    }

    localStorage.removeItem("appointmentData");
    localStorage.removeItem("joined");
  }, [socket, profileData, setIsOpen]);

  const callDeadlineRef = useRef(0);
  const callPausedRemainingRef = useRef(null);

  // Auto-cancel if consultant never answers — pause while consultant is testing devices
  useEffect(() => {
    if (!isOpen || consultantJoined) {
      callDeadlineRef.current = 0;
      callPausedRemainingRef.current = null;
      return undefined;
    }
    if (!callDeadlineRef.current) {
      callDeadlineRef.current = Date.now() + 60000;
    }
    if (isConsultantTesting) {
      if (callPausedRemainingRef.current == null) {
        callPausedRemainingRef.current = Math.max(5000, callDeadlineRef.current - Date.now());
      }
      return undefined;
    }
    if (callPausedRemainingRef.current != null) {
      callDeadlineRef.current = Date.now() + callPausedRemainingRef.current;
      callPausedRemainingRef.current = null;
    }
    const remaining = Math.max(250, callDeadlineRef.current - Date.now());
    const timeoutId = setTimeout(() => {
      if (joinedRef.current) return;
      const appt = appointmentRef.current || appointmentData || {};
      const isInstant = isInstantAppointment(appt);
      if (isInstant) {
        toast.error("Consultant did not answer. The call is cancelled.", { autoClose: 6000 });
      } else {
        toast.info("Consultant is currently unavailable. You can retry joining until the appointment end time.", { autoClose: 6000 });
      }
      handleJoinLater();
    }, remaining);
    return () => clearTimeout(timeoutId);
  }, [isOpen, consultantJoined, isConsultantTesting, handleJoinLater, appointmentData]);

  useSocket("vonageSessionRejectResponse", (data) => {
    if (data?.disconnect === true) {
      if (isOpen && !joinedRef.current) {
        const appt = appointmentRef.current || appointmentData || {};
        const isInstant = isInstantAppointment(appt);
        if (isInstant) {
          toast.error("Consultant declined the call.", { autoClose: 6000 });
        } else {
          toast.info("Consultant is currently unavailable. You can retry joining until the appointment end time.", { autoClose: 6000 });
        }
      }
      setIsOpen(false);
      stopAllCallMedia();
      stopAllRegisteredMedia();
      localStorage.removeItem("joined");
      localStorage.removeItem("appointmentData");
    }
  });

  // Stop any client-side camera/mic stream when modal closes without entering call
  useEffect(() => {
    if (!isOpen && !joinedRef.current) {
      stopAllCallMedia();
      stopAllRegisteredMedia();
    }
  }, [isOpen]);

  // Client waiting screen → always show CONSULTANT name + photo
  const consultantPerson =
    appointmentData?.callerData?.consultant || appointmentData?.consultant || {};
  const consultantName =
    consultantPerson?.name ||
    `${consultantPerson?.firstName || ""} ${consultantPerson?.lastName || ""}`.trim() ||
    "your consultant";
  const mode = appointmentData?.mode || "video";
  const consultantImage =
    (typeof consultantPerson?.image === "string" && consultantPerson.image) ||
    consultantPerson?.image?.url ||
    "";

  return (
    <Modal bodyClass={"!bg-white"} isOpen={isOpen} titleShow={false} size={"lg"}>
      <div className="flex flex-col justify-center items-center h-full p-6 ">
        <div className="flex items-center justify-center space-x-3 px-8 py-3 rounded-lg">
          <div className="flex flex-col items-center justify-center">
            {consultantImage ? (
              <img
                crossOrigin="anonymous"
                src={consultantImage}
                alt={consultantName}
                className="!w-32 !h-32 object-cover rounded-full mb-2 border p-1 bg-gray-100"
              />
            ) : null}
            <div className="text-xl font-semibold text-prontopsy-pink uppercase font-RobotoSlab">
              {consultantName}
            </div>
          </div>
        </div>
        <div className="text-center pb-3">
          <p className="text-base text-center">
            Your{" "}
            <span className="text-base text-prontopsy-dodgerBlue font-bold capitalize">{mode}</span>{" "}
            consult with{" "}
            <span className="text-base font-bold text-prontopsy-pink">{consultantName}</span> is
            about to begin. Please hold on while they connect to the call.
          </p>
          {isConsultantTesting ? (
            <p className="text-sm text-amber-600 mt-2 font-bold animate-pulse">
              Consultant is running a quick device verification check... Please hold on.
            </p>
          ) : (
            <p className="text-sm text-neutral-500 mt-2 animate-pulse">Waiting for consultant…</p>
          )}
        </div>
        <div className="flex space-x-6 text-xl">
          <button
            onClick={handleJoinLater}
            className="bg-red-500 flex gap-4 items-center text-white px-12 py-4 rounded-lg shadow-md hover:bg-red-600 transition duration-300 ease-in-out">
            <i className="fa-solid fa-xmark"></i>
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default JoinCallConfirmationModal;
